const hasIdentity = value => value !== null && typeof value !== 'undefined' && value !== ''

const normalizedProductId = link => (
    link && hasIdentity(link.productId) ? String(link.productId) : null
)

const findPrimaryProductLink = (links, extractedProductId) => {
    if (!hasIdentity(extractedProductId)) {
        return null
    }

    const candidates = links
        .map((link, sourceIndex) => ({ link, sourceIndex }))
        .filter(item => item.link
            && hasIdentity(item.link.extractedProductId)
            && String(item.link.extractedProductId) === String(extractedProductId))

    return candidates.find(item => item.link.status === 'accepted') || candidates[0] || null
}

const duplicateProductIds = links => {
    const counts = (Array.isArray(links) ? links : []).reduce((result, link) => {
        const productId = normalizedProductId(link)
        if (productId) {
            return {
                ...result,
                [productId]: (result[productId] || 0) + 1,
            }
        }
        return result
    }, {})

    return Object.keys(counts).filter(productId => counts[productId] > 1)
}

const buildProductReviewViewModel = (
    products,
    links,
    summariesById,
    missingProductIds,
    checkerRegistry,
) => {
    const productRows = Array.isArray(products) ? products : []
    const linkRows = Array.isArray(links) ? links : []
    const missingIds = (Array.isArray(missingProductIds) ? missingProductIds : []).map(String)
    const duplicates = duplicateProductIds(linkRows)
    const assignedLinkIndexes = new Set()

    const items = productRows.map((product, index) => {
        const linked = findPrimaryProductLink(linkRows, product && product.extractedProductId)
        if (linked) {
            assignedLinkIndexes.add(linked.sourceIndex)
        }
        const productId = normalizedProductId(linked && linked.link)
        const summary = productId ? summariesById[productId] || null : null
        const evaluation = checkerRegistry.evaluate({
            product: product || {},
            link: linked ? linked.link : null,
            summary,
            duplicateProductId: productId ? duplicates.includes(productId) : false,
            missingProduct: productId ? missingIds.includes(productId) : false,
        })

        return {
            index,
            rowKey: hasIdentity(product && product.extractedProductId)
                ? `product-${product.extractedProductId}`
                : `product-index-${index}`,
            product: product || {},
            link: linked ? linked.link : null,
            linkIndex: linked ? linked.sourceIndex : null,
            summary,
            evaluation,
        }
    })

    const additionalLinks = linkRows
        .map((link, sourceIndex) => ({ link, sourceIndex }))
        .filter(item => item.link && !hasIdentity(item.link.extractedProductId))
        .map(item => {
            const productId = normalizedProductId(item.link)
            return {
                ...item,
                summary: productId ? summariesById[productId] || null : null,
                duplicateProductId: productId ? duplicates.includes(productId) : false,
                missingProduct: productId ? missingIds.includes(productId) : false,
            }
        })

    const orphanedLinks = linkRows
        .map((link, sourceIndex) => ({ link, sourceIndex }))
        .filter(item => item.link
            && hasIdentity(item.link.extractedProductId)
            && !assignedLinkIndexes.has(item.sourceIndex))
        .map(item => {
            const productId = normalizedProductId(item.link)
            return {
                ...item,
                summary: productId ? summariesById[productId] || null : null,
                duplicateProductId: productId ? duplicates.includes(productId) : false,
                missingProduct: productId ? missingIds.includes(productId) : false,
            }
        })

    return {
        items,
        additionalLinks,
        orphanedLinks,
        duplicateProductIds: duplicates,
    }
}

export {
    buildProductReviewViewModel,
    duplicateProductIds,
    findPrimaryProductLink,
    hasIdentity,
    normalizedProductId,
}
